/**
 * Invoice-style settlement footer rendered as the final row of the
 * line-item table.
 *
 * Per-side layout (one Box per side):
 *  - 30% column: boxed metadata panel (note + Updated date)
 *  - 70% column: settlement summary (Subtotal / Tax annotation /
 *    Sale total / Less section / Amount due hero / state)
 *
 * Settlement entries are paired across sides by ``kind`` (payment vs
 * credit) + amount tolerance so both sides have the same number of
 * settlement rows. Combined with consistent line heights and the
 * ``nowrap`` ellipsis on long labels, this keeps ``Amount due``
 * vertically aligned without needing CSS subgrid - LS and Xero side
 * cells are placed in the same outer grid row so their natural heights
 * match.
 *
 * Delta chips appear only on the Xero side. LS credit-workaround lines
 * pick up an italic ``(line item)`` suffix to distinguish them from
 * real Xero credit notes.
 */
import React from 'react'
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  formatBusinessDate,
  formatCurrency,
  formatDateTime,
} from '../../../../utils/inspectorFormatters'
import {
  COMPARE_COLORS,
  COMPARE_NUM_FONT_SX,
  renderDeltaChip,
} from '../../../../components/inspector/shared/compareSalesPalette'
import { poolwerxColors } from '../../../../theme/colors'
import {
  handleLightspeedLinkClick,
  handleXeroLinkClick,
} from '../../../../utils/externalLinks'
import CreditNoteBadge from '../../../../components/shared/CreditNoteBadge'
import {
  AmountDueState,
  PaymentEntry,
  PaymentEntryPair,
  pairPaymentEntries,
  SaleCompareView,
} from './saleCompareView'
import type { CompareSalesData } from './useCompareSalesData'

// Re-export the shared Compare numeric stack so the footer reads as
// the same surface as the line-item table + exceptions table.
const TABULAR_NUMS = COMPARE_NUM_FONT_SX
const FOOTER_ROW_BG = '#FAFAFA'
// Soft note-card palette - lighter than the surrounding footer so the
// note reads as an inset bubble, not as a banner / header.
const META_PANEL_BG = '#FAFBFC'
const META_PANEL_BORDER = '1px solid #E5E7EB'
const META_TEXT = '#6B7280'
const META_ICON = '#9CA3AF'
const META_HOVER_BG = '#F1F3F5'

const AMOUNT_DUE_LABEL: Record<AmountDueState, string> = {
  paid_in_full: 'Paid in full',
  outstanding: 'Outstanding',
  overpaid: 'Overpaid',
}

const amountDueColour = (state: AmountDueState): string => {
  if (state === 'paid_in_full') return '#2E7D32'
  return COMPARE_COLORS.mismatchBorder
}

const NOTE_CLAMP_LINES = 3

/**
 * Settlement-summary font-size tiers. One place to bump the entire
 * totals area up or down. Note + Updated date in the metadata column
 * are intentionally NOT in this list - the metadata is independent.
 */
const FS = {
  /** Tiny annotations - sub-tax line, less-row date, state label,
   *  placeholder text, less-row entry date, line-item workaround
   *  suffix. */
  tiny: '0.78rem',
  /** Less header + less-row entry label + less-row amount. */
  small: '0.85rem',
  /** Subtotal label/value (non-emphasised). */
  normal: '0.9rem',
  /** Sale total label/value (emphasised). */
  strong: '0.95rem',
  /** Amount due label. */
  heroLabel: '1.05rem',
  /** Amount due value (hero). */
  heroValue: '1.2rem',
} as const

/** Plain-white gutter cell shared with the line-item table above. */
const GUTTER_CELL_SX = {
  width: 48,
  borderTop: '0 !important',
  borderBottom: '0 !important',
  borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#FFFFFF',
  padding: 0,
} as const

// ============================================================
// Metadata panel - boxed note + Updated date
// ============================================================

interface MetadataPanelProps {
  side: 'ls' | 'xero'
  note: string | null
  noteExpanded: boolean
  onToggleNote: () => void
  updatedAt: string | null | undefined
  updatedDateMismatch: boolean
  /** Signed day delta (this side - other side). Used in the Updated
   *  tooltip when the two sides differ so the analyst sees how far
   *  apart the timestamps are at a glance. */
  updatedDiffDays: number | null
}

/** Whole-day signed delta between two ISO timestamps, ``a - b``.
 *  Returns null when either side is missing or unparseable. */
const dayDelta = (
  a: string | null | undefined,
  b: string | null | undefined,
): number | null => {
  if (!a || !b) return null
  const aMs = Date.parse(a)
  const bMs = Date.parse(b)
  if (Number.isNaN(aMs) || Number.isNaN(bMs)) return null
  // ``Math.round`` so a sub-second difference doesn't round to "1 day".
  const days = (aMs - bMs) / 86_400_000
  return Math.round(days)
}

/** Format ``(+N days)`` / ``(-N days)`` for the Updated tooltip. */
const formatDaysDelta = (days: number): string => {
  if (days === 0) return '(same day)'
  const sign = days > 0 ? '+' : ''
  return `(${sign}${days} day${Math.abs(days) === 1 ? '' : 's'})`
}

const MetadataPanel: React.FC<MetadataPanelProps> = ({
  side,
  note,
  noteExpanded,
  onToggleNote,
  updatedAt,
  updatedDateMismatch,
  updatedDiffDays,
}) => {
  const hasNote = !!note
  const hasUpdated = !!updatedAt

  // Measure whether the clamped note would overflow. The chevron +
  // click affordance only render when expanding actually reveals more
  // text - short notes that already fit get a plain, static look.
  const noteRef = React.useRef<HTMLSpanElement | null>(null)
  const [needsExpand, setNeedsExpand] = React.useState(false)
  React.useEffect(() => {
    if (!hasNote || !noteRef.current) return
    const el = noteRef.current
    const measure = () => {
      // Only meaningful when collapsed - when expanded, the clamp is
      // off so scrollHeight == clientHeight. We keep the previously
      // measured value during expansion so the chevron stays visible.
      if (!noteExpanded) {
        setNeedsExpand(el.scrollHeight > el.clientHeight + 1)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [note, noteExpanded, hasNote])

  const interactive = needsExpand || noteExpanded

  if (!hasNote && !hasUpdated) return null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        // Fill the side cell vertically so the Updated date can pin to
        // the very bottom of the metadata column - lines up with the
        // "paid in full / outstanding / overpaid" state text on the
        // right.
        height: '100%',
        rowGap: 0.5,
      }}
      data-meta-panel={side}
    >
      {hasNote && (
        <Stack
          // Whole note area is clickable to toggle - but only when the
          // text actually overflows the clamp (or is currently
          // expanded). Short notes render as a plain static box.
          direction="row"
          spacing={0.75}
          alignItems="flex-start"
          role={interactive ? 'button' : undefined}
          tabIndex={interactive ? 0 : undefined}
          aria-expanded={interactive ? noteExpanded : undefined}
          aria-label={
            interactive
              ? noteExpanded
                ? 'Collapse note'
                : 'Expand note'
              : undefined
          }
          onClick={interactive ? onToggleNote : undefined}
          onKeyDown={
            interactive
              ? (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggleNote()
                  }
                }
              : undefined
          }
          sx={{
            minWidth: 0,
            cursor: interactive ? 'pointer' : 'default',
            userSelect: 'none',
            backgroundColor: META_PANEL_BG,
            border: META_PANEL_BORDER,
            borderRadius: 1,
            // Compact note-card padding: 6px vertical / 8px horizontal.
            px: '8px',
            py: '6px',
            transition: 'background-color 120ms',
            ...(interactive
              ? {
                  '&:hover': { backgroundColor: META_HOVER_BG },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 1,
                  },
                }
              : {}),
          }}
        >
          <StickyNote2OutlinedIcon
            sx={{
              fontSize: 14,
              color: META_ICON,
              flexShrink: 0,
              mt: 0.125,
            }}
          />
          <Typography
            ref={noteRef}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '0.72rem',
              color: META_TEXT,
              fontWeight: 400,
              lineHeight: 1.4,
              whiteSpace: 'pre-line',
              ...(noteExpanded
                ? {}
                : {
                    display: '-webkit-box',
                    WebkitLineClamp: NOTE_CLAMP_LINES,
                    WebkitBoxOrient: 'vertical' as const,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }),
            }}
          >
            {note}
          </Typography>
          {interactive &&
            (noteExpanded ? (
              <KeyboardArrowUpIcon
                sx={{ fontSize: 16, color: META_ICON, flexShrink: 0, mt: 0.125 }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{ fontSize: 16, color: META_ICON, flexShrink: 0, mt: 0.125 }}
              />
            ))}
        </Stack>
      )}
      {hasUpdated && (
        <Tooltip
          title={
            updatedDateMismatch
              ? `${formatDateTime(updatedAt!)} - LS and Xero updated dates differ${
                  updatedDiffDays != null
                    ? ` ${formatDaysDelta(updatedDiffDays)}`
                    : ''
                }`
              : formatDateTime(updatedAt!)
          }
          arrow
          placement="top"
        >
          <Stack
            direction="row"
            spacing={0.4}
            alignItems="center"
            sx={{
              cursor: 'help',
              alignSelf: 'flex-start',
              // Pin to the bottom of the metadata column so it lines up
              // with the Amount due state line on the right. No
              // background or border - this is tiny invoice metadata.
              mt: 'auto',
              pt: 0.75,
            }}
          >
            {updatedDateMismatch && (
              <WarningAmberIcon
                sx={{
                  fontSize: 12,
                  color: COMPARE_COLORS.mismatchBorder,
                }}
              />
            )}
            <EventOutlinedIcon
              sx={{
                fontSize: 12,
                color: updatedDateMismatch
                  ? COMPARE_COLORS.mismatchBorder
                  : META_ICON,
              }}
            />
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 400,
                color: updatedDateMismatch
                  ? COMPARE_COLORS.mismatchBorder
                  : META_ICON,
                whiteSpace: 'nowrap',
              }}
            >
              Updated {formatBusinessDate(updatedAt!)}
            </Typography>
          </Stack>
        </Tooltip>
      )}
    </Box>
  )
}

// ============================================================
// Settlement summary - one side's stack of totals + Less rows
// ============================================================

interface SettlementSummaryProps {
  side: 'ls' | 'xero'
  view: SaleCompareView
  /** Pre-paired settlement entries (same array on both sides) so the
   *  row count matches and Amount due lines up vertically across
   *  LS / Xero halves. */
  settlementPairs: PaymentEntryPair[]
  saleTotalDiff: number
  amountDueDiff: number
  saleTotalMatches: boolean
  amountDueMatches: boolean
  xeroMissing: boolean
}

const SettlementSummary: React.FC<SettlementSummaryProps> = ({
  side,
  view,
  settlementPairs,
  saleTotalDiff,
  amountDueDiff,
  saleTotalMatches,
  amountDueMatches,
  xeroMissing,
}) => {
  // When Xero is missing entirely, render an empty placeholder column
  // so the LS side still gets its 70% of the side cell. Outer cell
  // already shows the EmptySidePanel notice via the line-item table.
  if (xeroMissing && side === 'xero') {
    return <Box />
  }

  return (
    <Box
      sx={{
        display: 'grid',
        // label flexes; delta + value reserved on both sides so the
        // value column lines up across rows.
        gridTemplateColumns: 'minmax(0, 1fr) 56px 110px',
        columnGap: 1.25,
        rowGap: 0.4,
        alignItems: 'center',
      }}
      data-summary={side}
    >
      <SummaryRow
        label="Subtotal"
        value={view.saleTotal}
        emphasis="normal"
      />
      <SubAnnotationRow label="Includes tax" value={view.totalTax} />
      <SummaryRow
        label="Sale total"
        value={view.saleTotal}
        emphasis="strong"
        showDelta={side === 'xero' && !saleTotalMatches}
        diff={saleTotalDiff}
      />
      <SectionDivider weight="medium" />
      <LessHeader />
      {settlementPairs.length === 0 ? (
        <LessPlaceholder />
      ) : (
        settlementPairs.map((pair, i) => (
          <LessEntryRow
            key={`less-${i}`}
            side={side}
            entry={side === 'ls' ? pair.ls : pair.xero}
          />
        ))
      )}
      <SectionDivider weight="strong" />
      <AmountDueRow
        value={view.amountDue}
        state={view.amountDueState}
        showDelta={side === 'xero' && !amountDueMatches}
        diff={amountDueDiff}
      />
      <StateRow state={view.amountDueState} side={side} />
    </Box>
  )
}

// ============================================================
// Row primitives - each fills its 3-col grid row
// ============================================================

interface SummaryRowProps {
  label: string
  value: number
  emphasis: 'normal' | 'strong'
  showDelta?: boolean
  diff?: number
}

const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  value,
  emphasis,
  showDelta = false,
  diff = 0,
}) => {
  const fontSize = emphasis === 'strong' ? FS.strong : FS.normal
  const weight = emphasis === 'strong' ? 700 : 500
  const valueColour = showDelta ? COMPARE_COLORS.mismatchBorder : undefined
  return (
    <>
      <Typography sx={{ fontSize, fontWeight: weight }}>{label}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {showDelta ? <DeltaPill diff={diff} /> : null}
      </Box>
      <Typography
        sx={{
          ...TABULAR_NUMS,
          fontSize,
          fontWeight: weight,
          textAlign: 'right',
          color: valueColour,
        }}
        data-value="true"
      >
        {formatCurrency(value)}
      </Typography>
    </>
  )
}

const SubAnnotationRow: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <>
    <Typography
      sx={{ fontSize: FS.tiny, color: 'text.secondary', pl: 1.25 }}
    >
      {label}
    </Typography>
    <Box />
    <Typography
      sx={{
        ...TABULAR_NUMS,
        fontSize: FS.tiny,
        color: 'text.secondary',
        textAlign: 'right',
      }}
    >
      {formatCurrency(value)}
    </Typography>
  </>
)

const SectionDivider: React.FC<{ weight: 'medium' | 'strong' }> = ({ weight }) => (
  <Box
    sx={{
      gridColumn: '1 / -1',
      borderTop:
        weight === 'strong'
          ? '2px solid rgba(0,0,0,0.45)'
          : '1px solid rgba(0,0,0,0.20)',
      my: 0.5,
    }}
  />
)

const LessHeader: React.FC = () => (
  <Typography
    sx={{
      gridColumn: '1 / -1',
      fontSize: FS.small,
      fontWeight: 600,
      color: 'text.primary',
      pt: 0.25,
    }}
    data-less-header="true"
  >
    Less:
  </Typography>
)

const LessPlaceholder: React.FC = () => (
  <Typography
    sx={{
      gridColumn: '1 / -1',
      fontSize: FS.tiny,
      color: 'text.disabled',
      fontStyle: 'italic',
      pl: 1.5,
    }}
    data-less-placeholder="true"
  >
    No payments/credits applied to this sale
  </Typography>
)

const LessEntryRow: React.FC<{
  side: 'ls' | 'xero'
  entry: PaymentEntry | null
}> = ({ side, entry }) => {
  // Empty side - paired pair where the other side has the row.
  // Render a muted placeholder so the row stays present and the
  // sibling LS / Xero row keeps its vertical alignment.
  if (!entry) {
    return (
      <>
        <Typography
          sx={{
            fontSize: FS.tiny,
            color: 'text.disabled',
            fontStyle: 'italic',
            pl: 1.5,
          }}
          data-side={side}
          data-less-placeholder="true"
        >
          -
        </Typography>
        <Box />
        <Box />
      </>
    )
  }

  const isCredit = entry.kind === 'credit'
  const isLsWorkaround = isCredit && side === 'ls'
  const dateText = entry.date ? formatBusinessDate(entry.date) : null
  const rawLabel = entry.label.replace(/^Credit\s+/, '')
  const entryLabel = isCredit
    ? side === 'xero'
      ? `Credit Note ${rawLabel}`
      : `Credit ${rawLabel}`
    : entry.label

  const labelTooltip =
    `${entryLabel}${dateText ? ` (${dateText})` : ''}${isLsWorkaround ? ' - line item (Lightspeed workaround)' : ''}`

  // Hard-truncate the entry NAME to 16 chars (suffix ``...``) so the
  // settlement column reads at a glance and never wraps. The date is
  // concatenated after the truncated name; the whole "name (date)"
  // string is a single ``<a>`` (or ``<span>``) - no nested spans.
  // Full text remains in the tooltip above.
  const truncName =
    entryLabel.length > 16 ? `${entryLabel.slice(0, 16)}...` : entryLabel
  const displayText = dateText
    ? `${truncName} (${dateText})`
    : truncName

  const linkSx = {
    fontSize: FS.small,
    whiteSpace: 'nowrap' as const,
    // Let the label itself ellipsise when the column is tight so the
    // trailing CN badge / (line item) suffix never gets clipped by the
    // row's overflow:hidden.
    minWidth: 0,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  } as const
  // Route the click through the central popup handler so the
  // Xero / Lightspeed link opens in the same sized popup window the
  // invoice link in the header uses.
  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    if (!entry.link) return
    if (side === 'xero') {
      handleXeroLinkClick(e, entry.link, entryLabel)
    } else {
      handleLightspeedLinkClick(e, entry.link, entryLabel)
    }
  }
  const linkedRow = entry.link ? (
    <Box
      component="a"
      href={entry.link}
      onClick={onLinkClick}
      sx={{
        ...linkSx,
        color: poolwerxColors.teal,
        textDecoration: 'underline',
      }}
    >
      {displayText}
    </Box>
  ) : (
    <Box component="span" sx={{ ...linkSx, color: 'text.primary' }}>
      {displayText}
    </Box>
  )

  return (
    <>
      <Tooltip title={labelTooltip} arrow placement="top" disableInteractive>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.5,
            minWidth: 0,
            pl: 1.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
          data-side={side}
        >
          {linkedRow}
          {isCredit && (
            <Box component="span" sx={{ flexShrink: 0, alignSelf: 'center' }}>
              <CreditNoteBadge
                title={
                  isLsWorkaround
                    ? 'Credit note (applied as a Lightspeed line item)'
                    : 'Credit note'
                }
              />
            </Box>
          )}
          {isLsWorkaround && (
            <Typography
              component="span"
              sx={{
                fontSize: FS.tiny,
                color: 'text.disabled',
                fontStyle: 'italic',
                flexShrink: 0,
              }}
            >
              (line item)
            </Typography>
          )}
        </Box>
      </Tooltip>
      <Box />
      <Typography
        sx={{
          ...TABULAR_NUMS,
          fontSize: FS.small,
          fontWeight: 500,
          textAlign: 'right',
          alignSelf: 'center',
        }}
      >
        {formatCurrency(entry.amount)}
      </Typography>
    </>
  )
}

const AmountDueRow: React.FC<{
  value: number
  state: AmountDueState
  showDelta: boolean
  diff: number
}> = ({ value, state, showDelta, diff }) => {
  const colour = amountDueColour(state)
  return (
    <>
      <Typography sx={{ fontSize: FS.heroLabel, fontWeight: 700 }}>
        Amount due
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {showDelta ? <DeltaPill diff={diff} /> : null}
      </Box>
      <Typography
        sx={{
          ...TABULAR_NUMS,
          fontSize: FS.heroValue,
          fontWeight: 800,
          textAlign: 'right',
          color: colour,
        }}
        data-value="true"
        data-row="amount_due"
      >
        {formatCurrency(value)}
      </Typography>
    </>
  )
}

const StateRow: React.FC<{ state: AmountDueState; side: 'ls' | 'xero' }> = ({
  state,
  side,
}) => (
  <>
    <Box />
    <Box />
    <Typography
      variant="caption"
      sx={{
        fontSize: FS.tiny,
        color: 'text.secondary',
        textAlign: 'right',
        lineHeight: 1.3,
      }}
      data-amount-due-state={state}
      data-side={side}
    >
      {AMOUNT_DUE_LABEL[state]}
    </Typography>
  </>
)

// Totals-block delta chip uses the same default font sizing as the
// line-item delta chip ("0.7rem" body, "12px" arrow) so the two
// surfaces share the same visual weight.
const DeltaPill: React.FC<{ diff: number }> = ({ diff }) => {
  const chip = renderDeltaChip(diff, true)
  if (!chip) return null
  return chip
}

// ============================================================
// SideContainer - one side's [metadata 30% | summary 70%]
// ============================================================

interface SideContainerProps {
  side: 'ls' | 'xero'
  metadata: MetadataPanelProps
  summary: SettlementSummaryProps
}

const SideContainer: React.FC<SideContainerProps> = ({
  side,
  metadata,
  summary,
}) => (
  <Box
    sx={{
      display: 'grid',
      // Metadata column ~36% of the side (20% wider than the previous
      // 30%). The remaining ~64% goes to the settlement summary.
      gridTemplateColumns: '36% 1fr',
      // Gap doubled from 16px so the boxed note panel doesn't crowd
      // the totals to its right.
      columnGap: 4,
      // Padding mirrors the line-item TableCell defaults
      // (MUI size="small" = 16px horizontal / 6px vertical) so the
      // footer doesn't drift left/right relative to the table above.
      px: 2,
      py: 1.5,
      // ``stretch`` lets the metadata column fill the full side cell
      // height so the Updated date inside can pin to the bottom and
      // line up with the ``paid in full`` state text on the right.
      alignItems: 'stretch',
    }}
    data-side-container={side}
  >
    <MetadataPanel {...metadata} />
    <SettlementSummary {...summary} />
  </Box>
)

// ============================================================
// FooterRow - one TableRow with LS | gutter | Xero TableCells
// ============================================================

interface CompareSalesFooterRowProps {
  data: CompareSalesData
  lsNotesExpanded: boolean
  xeroNotesExpanded: boolean
  onToggleLsNotes: () => void
  onToggleXeroNotes: () => void
}

const CompareSalesFooterRow: React.FC<CompareSalesFooterRowProps> = ({
  data,
  lsNotesExpanded,
  xeroNotesExpanded,
  onToggleLsNotes,
  onToggleXeroNotes,
}) => {
  const {
    lsView,
    xeroView,
    lightspeed,
    xero,
    xeroMissing,
    lsNote,
    xeroNote,
    updatedDateMismatch,
    saleTotalMatch,
    amountDueMatch,
  } = data

  // Pair settlements by kind (payment vs credit) + amount tolerance so
  // both sides receive the SAME list. Each side renders pair.ls or
  // pair.xero - empty side gets a muted placeholder. Result: both
  // sides have identical row counts and Amount due lines up.
  const lsPayments = lsView.paymentEntries.filter(e => e.kind === 'payment')
  const xeroPayments = xeroMissing
    ? []
    : xeroView.paymentEntries.filter(e => e.kind === 'payment')
  const lsCredits = lsView.paymentEntries.filter(e => e.kind === 'credit')
  const xeroCredits = xeroMissing
    ? []
    : xeroView.paymentEntries.filter(e => e.kind === 'credit')
  const settlementPairs = [
    ...pairPaymentEntries(lsPayments, xeroPayments),
    ...pairPaymentEntries(lsCredits, xeroCredits),
  ]

  const saleTotalDiff = xeroView.saleTotal - lsView.saleTotal
  const amountDueDiff = xeroView.amountDue - lsView.amountDue

  // Days diff is computed once per row and inverted per side so each
  // tooltip reads as "this side relative to the other".
  const lsToXeroDays = dayDelta(
    lightspeed.ls_updated_at,
    xero.xero_updated_date_utc,
  )
  const xeroToLsDays =
    lsToXeroDays != null ? -lsToXeroDays : null

  const lsSide: SideContainerProps = {
    side: 'ls',
    metadata: {
      side: 'ls',
      note: lsNote,
      noteExpanded: lsNotesExpanded,
      onToggleNote: onToggleLsNotes,
      updatedAt: lightspeed.ls_updated_at,
      updatedDateMismatch,
      updatedDiffDays: lsToXeroDays,
    },
    summary: {
      side: 'ls',
      view: lsView,
      settlementPairs,
      saleTotalDiff,
      amountDueDiff,
      saleTotalMatches: saleTotalMatch,
      amountDueMatches: amountDueMatch,
      xeroMissing,
    },
  }
  const xeroSide: SideContainerProps = {
    side: 'xero',
    metadata: {
      side: 'xero',
      note: xeroNote,
      noteExpanded: xeroNotesExpanded,
      onToggleNote: onToggleXeroNotes,
      updatedAt: xero.xero_updated_date_utc,
      updatedDateMismatch,
      updatedDiffDays: xeroToLsDays,
    },
    summary: {
      side: 'xero',
      view: xeroView,
      settlementPairs,
      saleTotalDiff,
      amountDueDiff,
      saleTotalMatches: saleTotalMatch,
      amountDueMatches: amountDueMatch,
      xeroMissing,
    },
  }

  // Footer cells own their top + bottom borders so the centre gutter
  // column stays uncrossed end-to-end.
  const cellSx = {
    backgroundColor: FOOTER_ROW_BG,
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    p: 0,
    verticalAlign: 'top',
  } as const

  return (
    <TableRow data-footer-row="true">
      <TableCell colSpan={5} sx={cellSx}>
        <SideContainer {...lsSide} />
      </TableCell>
      <TableCell sx={GUTTER_CELL_SX} data-footer-gutter="true" />
      <TableCell colSpan={5} sx={cellSx}>
        <SideContainer {...xeroSide} />
      </TableCell>
    </TableRow>
  )
}

export default CompareSalesFooterRow

export { GUTTER_CELL_SX }
